import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MemberStatusBadge } from '@/components/tenant-members/MemberStatusBadge'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS, type TenantMember, type TenantMemberSortColumn } from '@/types'
import { Trash2 } from 'lucide-react'
import { useTheme } from 'next-themes'

interface TenantMembersTableProps {
  members: TenantMember[]
  isLoading: boolean
  onRemove?: (member: TenantMember) => void
  sortBy: TenantMemberSortColumn
  sortDirection: 'asc' | 'desc'
  onSortChange: (column: TenantMemberSortColumn) => void
}

const AVATAR_COLORS = ['#1d3461', '#1a4731', '#4a1942', '#2d4a1e', '#1a3d4a', '#3d2b1a']
const AVATAR_COLORS_DARK = ['#C7D2F5', '#C7E8CF', '#EAC7E0', '#DCEAC0', '#C5E7EE', '#EAD8C0']

function getAvatarColor(name: string, isDark: boolean): string {
  const palette = isDark ? AVATAR_COLORS_DARK : AVATAR_COLORS
  const sum = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return palette[sum % palette.length]
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function UserAvatar({ name }: { name: string }) {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  if (!name) {
    return (
      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs font-semibold shrink-0">
        ?
      </div>
    )
  }
  return (
    <div
      className="h-8 w-8 rounded-full flex items-center justify-center text-background text-xs font-semibold shrink-0"
      style={{ backgroundColor: getAvatarColor(name, isDark) }}
    >
      {getInitials(name)}
    </div>
  )
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  const date = d.toLocaleDateString('en-GB')
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return `${date}, ${time}`
}

export function TenantMembersTable({ members, isLoading, onRemove, sortBy, sortDirection, onSortChange }: TenantMembersTableProps) {
  const sortProps = { sortBy, sortDirection, defaultDirections: TENANT_MEMBER_DEFAULT_SORT_DIRECTIONS, onSortChange }

  return (
    <Table aria-label="Tenant members">
      <TableHeader>
        <TableRow>
          <SortableTableHead column="userName" label="User Name" {...sortProps} />
          <SortableTableHead column="email" label="Email" {...sortProps} />
          <SortableTableHead column="role" label="Role" className="w-[160px]" {...sortProps} />
          <SortableTableHead column="status" label="Status" className="w-[130px]" {...sortProps} />
          <SortableTableHead column="joined" label="Joined" className="w-[190px]" {...sortProps} />
          <TableHead className="w-[140px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </TableCell>
              {Array.from({ length: 5 }).map((_, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : members.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
              No members found.
            </TableCell>
          </TableRow>
        ) : (
          members.map((member) => {
            const displayLabel = member.displayName || member.invitedEmail
            return (
              <TableRow key={member.id} data-testid="tenant-member-row">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <UserAvatar name={displayLabel} />
                    <span className="min-w-0 font-medium">{displayLabel}</span>
                  </div>
                </TableCell>
                <TableCell className="text-foreground">{member.invitedEmail}</TableCell>
                <TableCell>{member.role}</TableCell>
                <TableCell>
                  <MemberStatusBadge status={member.status} />
                </TableCell>
                <TableCell className="text-foreground">{formatDate(member.joinedAt)}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="iconSm"
                    className="text-muted-foreground"
                    aria-label="Remove member"
                    data-testid="remove-member-button"
                    onClick={() => onRemove?.(member)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            )
          })
        )}
      </TableBody>
    </Table>
  )
}
